import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react';
import Layout from '@/components/Layout';
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Search, X, Trash2, Plus } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import PaginationFooter from '@/components/PaginationFooter';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/hooks/useI18n';

interface RevenueCategory { id: string; name: string; }
interface UnitOfMeasure { id: string; name: string; abbreviation: string; unit_type: string; }

interface Product {
    id: string;
    name: string;
    commodity_code?: string;
    part_number?: string;
    revenue_category_id?: string;
    revenue_category?: RevenueCategory;
    base_unit_id?: string;
    base_unit?: UnitOfMeasure;
    base_quantity?: number;
    is_active: boolean;
    status?: 'draft' | 'active' | 'inactive';
}

interface Pagination { page: number; total: number; pages: number; limit: number; }

interface EditForm {
    name: string;
    commodity_code: string;
    part_number: string;
    revenue_category_id: string | null;
    base_unit_id: string | null;
    base_quantity: number | null;
    is_active: boolean;
}

export default function SalesProducts() {
    const { t } = useI18n();
    const [products, setProducts] = useState<Product[]>([]);
    const [categories, setCategories] = useState<RevenueCategory[]>([]);
    const [baseUnits, setBaseUnits] = useState<UnitOfMeasure[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [showInactive, setShowInactive] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const [pagination, setPagination] = useState<Pagination>({ page: 1, total: 0, pages: 0, limit: 20 });

    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<EditForm>({ name: '', commodity_code: '', part_number: '', revenue_category_id: null, base_unit_id: null, base_quantity: null, is_active: true });

    // Simple create modal state
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState<EditForm>({ name: '', commodity_code: '', part_number: '', revenue_category_id: null, base_unit_id: null, base_quantity: null, is_active: true });

    useEffect(() => { fetchProducts(); fetchCategories(); fetchUnits(); }, [pagination.page, pagination.limit]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    useEffect(() => {
        if (pagination.page !== 1) setPagination(prev => ({ ...prev, page: 1 })); else fetchProducts();
    }, [debouncedSearchTerm, showInactive]);

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', String(pagination.page));
            params.append('limit', String(pagination.limit));
            if (!showInactive) params.append('is_active', 'true');
            params.append('is_sellable', 'true'); // only sellable products for Revenues
            if (debouncedSearchTerm.trim()) params.append('search', debouncedSearchTerm.trim());
            const res = await apiFetch(`/api/products/?${params.toString()}`);
            if (res.ok) {
                const data = await res.json();
                setProducts(data.products);
                if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
            }
        } finally { setLoading(false); }
    };

    const fetchCategories = async () => {
        const r = await apiFetch('/api/revenue-categories/');
        if (r.ok) setCategories(await r.json());
    };
    const fetchUnits = async () => {
        const r = await apiFetch('/api/units/');
        if (r.ok) setBaseUnits(await r.json());
    };

    const beginEdit = (p: Product) => {
        setEditingId(p.id);
        setEditForm({ name: p.name, commodity_code: p.commodity_code || '', part_number: p.part_number || '', revenue_category_id: p.revenue_category_id || null, base_unit_id: p.base_unit_id || null, base_quantity: p.base_quantity || null, is_active: p.is_active });
    };

    const saveEdit = async () => {
        if (!editingId) return;
        const body = { ...editForm } as any;
        // ensure sellable flag stays true when editing on sales page
        body.is_sellable = true;
        const res = await apiFetch(`/api/products/${editingId}`, { method: 'PATCH', body: JSON.stringify(body) });
        if (res.ok) { setEditingId(null); fetchProducts(); }
    };

    const deleteProduct = async (id: string, name: string) => {
        if (!confirm(`Deactivate \"${name}\"?`)) return;
        const r = await apiFetch(`/api/products/${id}`, { method: 'DELETE' });
        if (r.ok) fetchProducts();
    };

    const createProduct = async () => {
        const body = { ...createForm, is_sellable: true, is_purchasable: false };
        const res = await apiFetch('/api/products/', { method: 'POST', body: JSON.stringify(body) });
        if (res.ok) { setShowCreate(false); setCreateForm({ name: '', commodity_code: '', part_number: '', revenue_category_id: null, base_unit_id: null, base_quantity: null, is_active: true }); fetchProducts(); }
    };

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{t('products.title', 'common')}</h1>
                        <p className="text-muted-foreground">{t('products.sellableDescription', 'common')}</p>
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={() => setShowCreate(true)}>
                            <Plus className="w-4 h-4 mr-2" /> {t('products.addProduct', 'common')}
                        </Button>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <div className="flex-1 max-w-md relative">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder={t('products.searchPlaceholder', 'common')} className="pl-8" />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            {searchTerm && (
                                <Button variant="ghost" size="sm" className="h-auto p-1" onClick={() => setSearchTerm('')}>
                                    <X className="h-3 w-3" />
                                </Button>
                            )}
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="rounded border-zinc-300" />
                        {t('products.showInactive', 'common')}
                    </label>
                </div>

                <div className="min-h-[600px] relative">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('products.name', 'common')}</TableHead>
                                <TableHead>{t('products.commodityCode', 'common')}</TableHead>
                                <TableHead>{t('products.partNumber', 'common')}</TableHead>
                                <TableHead>{t('products.category', 'common')}</TableHead>
                                <TableHead>{t('products.unit', 'common')}</TableHead>
                                <TableHead>{t('products.quantity', 'common')}</TableHead>
                                <TableHead>{t('products.status', 'common')}</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                Array.from({ length: pagination.limit || 20 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-6 w-16 rounded-full" /></TableCell>
                                        <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                                    </TableRow>
                                ))
                            ) : products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={8} className="py-8 text-center text-muted-foreground">{t('products.noProductsFound', 'common')}</TableCell>
                                </TableRow>
                            ) : products.map(p => (
                                <TableRow key={p.id} className={`${!p.is_active ? 'opacity-60 bg-muted/20' : ''} ${editingId !== p.id ? 'cursor-pointer hover:bg-muted/50' : ''}`} onClick={() => editingId !== p.id && beginEdit(p)}>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} onClick={e => e.stopPropagation()} />
                                        ) : p.name}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <Input value={editForm.commodity_code} onChange={e => setEditForm({ ...editForm, commodity_code: e.target.value })} onClick={e => e.stopPropagation()} />
                                        ) : (p.commodity_code || '-')}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <Input value={editForm.part_number} onChange={e => setEditForm({ ...editForm, part_number: e.target.value })} onClick={e => e.stopPropagation()} />
                                        ) : (p.part_number || '-')}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <div onClick={e => e.stopPropagation()}>
                                                <Select value={editForm.revenue_category_id || 'none'} onValueChange={v => setEditForm({ ...editForm, revenue_category_id: v === 'none' ? null : v })}>
                                                    <SelectTrigger className="text-sm"><SelectValue placeholder={t('products.selectCategory', 'common')} /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">{t('products.none', 'common')}</SelectItem>
                                                        {categories.map(c => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ) : (
                                            <span>{p.revenue_category ? p.revenue_category.name : t('products.noCategory', 'common')}</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <div onClick={e => e.stopPropagation()}>
                                                <Select value={editForm.base_unit_id || 'none'} onValueChange={v => setEditForm({ ...editForm, base_unit_id: v === 'none' ? null : v })}>
                                                    <SelectTrigger className="text-sm"><SelectValue placeholder={t('products.selectUnit', 'common')} /></SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="none">{t('products.none', 'common')}</SelectItem>
                                                        {['volume', 'weight', 'count', 'length'].map(t => (
                                                            <div key={t}>
                                                                <div className="px-2 py-1 text-xs font-semibold text-gray-500 uppercase">{t}</div>
                                                                {baseUnits.filter(u => u.unit_type === t).map(u => (<SelectItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</SelectItem>))}
                                                            </div>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        ) : (
                                            <span>{p.base_unit ? `${p.base_unit.name} (${p.base_unit.abbreviation})` : '-'}</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <Input type="number" step="0.1" value={editForm.base_quantity || ''} onChange={e => setEditForm({ ...editForm, base_quantity: e.target.value ? parseFloat(e.target.value) : null })} onClick={e => e.stopPropagation()} />
                                        ) : (p.base_quantity ? p.base_quantity.toLocaleString() : '-')}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <div onClick={e => e.stopPropagation()}>
                                                <label className="flex items-center gap-2">
                                                    <input type="checkbox" className="rounded border-zinc-300" checked={editForm.is_active} onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })} />
                                                    <span className="text-sm">{t('products.active', 'common')}</span>
                                                </label>
                                            </div>
                                        ) : (
                                            <Badge variant={p.status === 'active' ? 'default' : 'secondary'}>{p.status === 'inactive' ? t('products.inactive', 'common') : t('products.active', 'common')}</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === p.id ? (
                                            <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                                                <Button size="sm" onClick={saveEdit}>{t('actions.save', 'common')}</Button>
                                                <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>{t('actions.cancel', 'common')}</Button>
                                            </div>
                                        ) : (
                                            <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); deleteProduct(p.id, p.name); }} className="text-destructive hover:text-destructive">
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>

                <PaginationFooter
                    pagination={pagination}
                    onPageChange={(page) => setPagination(prev => ({ ...prev, page }))}
                    onPageSizeChange={(limit) => setPagination(prev => ({ ...prev, limit, page: 1 }))}
                />

                {showCreate && (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setShowCreate(false)}>
                        <div className="bg-white rounded-lg p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
                            <h3 className="text-lg font-semibold mb-4">{t('products.addProduct', 'common')}</h3>
                            <div className="space-y-3">
                                <div className="space-y-1">
                                    <Label htmlFor="name">{t('products.name', 'common')} *</Label>
                                    <Input id="name" value={createForm.name} onChange={e => setCreateForm({ ...createForm, name: e.target.value })} required />
                                </div>
                                <div className="space-y-1">
                                    <Label>{t('products.commodityCodeVTSZ', 'common')}</Label>
                                    <Input value={createForm.commodity_code} onChange={e => setCreateForm({ ...createForm, commodity_code: e.target.value })} />
                                </div>
                                <div className="space-y-1">
                                    <Label>{t('products.partNumber', 'common')}</Label>
                                    <Input value={createForm.part_number} onChange={e => setCreateForm({ ...createForm, part_number: e.target.value })} />
                                </div>
                                <div className="flex gap-2 justify-end pt-2">
                                    <Button variant="outline" onClick={() => setShowCreate(false)}>{t('actions.cancel', 'common')}</Button>
                                    <Button onClick={createProduct}>{t('actions.create', 'common')}</Button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}


export const Route = createFileRoute('/sales/products')({
    component: SalesProducts,
})
